<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import CatalogAPI from 'dashboard/api/catalog';
import CatalogSourcesAPI from 'dashboard/api/catalogSources';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import PageEmptyState from 'escaleshop/shared/components/PageEmptyState.vue';

const { t } = useI18n();
const DASH = '—';

const products = ref([]);
const sources = ref([]);
const loading = ref(true);
const error = ref('');
const searchQuery = ref('');
const selectedSourceId = ref('');
const selectedStatus = ref('active');
const perPage = ref(20);
const currentPage = ref(1);
const total = ref(0);
const totalPages = ref(1);
const expandedId = ref(null);
const showFilterPanel = ref(false);
const showSortMenu = ref(false);
const activeSort = ref('title');
const activeDirection = ref('asc');

// pending filter state (applied only on "Aplicar filtros")
const pendingSourceId = ref('');
const pendingStatus = ref('active');

const debounceTimer = ref(null);

const sourceOptions = computed(() => [
  { label: t('CATALOG.PRODUCTS.ALL_SOURCES'), value: '' },
  ...sources.value.map(s => ({ label: s.name || s.reference || s.kind, value: String(s.id) })),
]);

const statusOptions = computed(() => [
  { label: t('CATALOG.PRODUCTS.STATUS.active'), value: 'active' },
  { label: t('CATALOG.PRODUCTS.STATUS.draft'), value: 'draft' },
  { label: t('CATALOG.PRODUCTS.STATUS.archived'), value: 'archived' },
  { label: t('CATALOG.PRODUCTS.ALL_STATUSES'), value: 'all' },
]);

const sortOptions = [
  { label: t('CATALOG.PRODUCTS.SORT.OPTIONS.TITLE'), value: 'title' },
  { label: t('CATALOG.PRODUCTS.SORT.OPTIONS.CREATED_AT'), value: 'created_at' },
];

const directionOptions = [
  { label: t('CATALOG.PRODUCTS.SORT.ORDER_OPTIONS.ASC'), value: 'asc' },
  { label: t('CATALOG.PRODUCTS.SORT.ORDER_OPTIONS.DESC'), value: 'desc' },
];

const activeSortLabel = computed(
  () => sortOptions.find(o => o.value === activeSort.value)?.label || activeSort.value
);

const activeDirectionLabel = computed(
  () => directionOptions.find(o => o.value === activeDirection.value)?.label || activeDirection.value
);

const sourceLabelFor = val =>
  sourceOptions.value.find(o => o.value === val)?.label || t('CATALOG.PRODUCTS.ALL_SOURCES');

const statusLabelFor = val =>
  statusOptions.value.find(o => o.value === val)?.label || val;

const hasActiveFilters = computed(
  () => !!selectedSourceId.value || selectedStatus.value !== 'active'
);

const fetchSources = async () => {
  try {
    const { data } = await CatalogSourcesAPI.list();
    sources.value = data.sources || [];
  } catch {
    sources.value = [];
  }
};

const fetchProducts = async (page = 1) => {
  try {
    loading.value = true;
    expandedId.value = null;
    const { data } = await CatalogAPI.getProducts({
      query: searchQuery.value,
      page,
      sourceId: selectedSourceId.value || null,
      status: selectedStatus.value,
      perPage: perPage.value,
      sort: activeSort.value,
      direction: activeDirection.value,
    });
    products.value = data.products || [];
    currentPage.value = data.page || page;
    total.value = data.total || 0;
    totalPages.value = data.total_pages || 1;
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchSources();
  await fetchProducts(1);
});

watch(searchQuery, () => {
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => fetchProducts(1), 300);
});

const openFilterPanel = () => {
  pendingSourceId.value = selectedSourceId.value;
  pendingStatus.value = selectedStatus.value;
  showFilterPanel.value = true;
};

const closeFilterPanel = () => {
  showFilterPanel.value = false;
};

const applyFilters = () => {
  selectedSourceId.value = pendingSourceId.value;
  selectedStatus.value = pendingStatus.value;
  showFilterPanel.value = false;
  fetchProducts(1);
};

const clearFilters = () => {
  pendingSourceId.value = '';
  pendingStatus.value = 'active';
  selectedSourceId.value = '';
  selectedStatus.value = 'active';
  showFilterPanel.value = false;
  fetchProducts(1);
};

const filterOutsideHandler = [
  closeFilterPanel,
  { ignore: ['#toggleCatalogFilterButton'] },
];

const handleSortChange = val => {
  activeSort.value = val;
  fetchProducts(1);
};

const handleDirectionChange = val => {
  activeDirection.value = val;
  fetchProducts(1);
};

const toggleExpand = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(Number(amount) || 0);

const priceRange = product => {
  const prices = product.variants?.map(v => Number(v.price)).filter(Boolean) || [];
  if (!prices.length) return '—';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const currency = product.variants?.[0]?.currency;
  return min === max
    ? formatCurrency(min, currency)
    : `${formatCurrency(min, currency)} – ${formatCurrency(max, currency)}`;
};

const productStatusText = s => {
  switch (s) {
    case 'active':
      return t('CATALOG.PRODUCTS.STATUS.active');
    case 'draft':
      return t('CATALOG.PRODUCTS.STATUS.draft');
    case 'archived':
      return t('CATALOG.PRODUCTS.STATUS.archived');
    default:
      return s;
  }
};

const sourceKindLabel = kind =>
  kind === 'shopify' ? 'Shopify' : kind;

const productStatusColor = s =>
  ({
    active: 'bg-n-teal-3 text-n-teal-11',
    draft: 'bg-n-amber-3 text-n-amber-11',
    archived: 'bg-n-solid-3 text-n-slate-10',
  }[s] || 'bg-n-solid-3 text-n-slate-10');
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1 overflow-hidden">

    <!-- Sticky header — same pattern as ContactHeader -->
    <header class="sticky top-0 z-10 px-6 lg:px-16 bg-n-surface-1">
      <div class="flex items-center justify-between w-full py-6 gap-2 mx-auto max-w-7xl">
        <span class="text-xl font-medium truncate text-n-slate-12">
          {{ t('CATALOG.PRODUCTS.TITLE') }}
        </span>

        <div class="flex items-center gap-4 flex-shrink-0">
          <!-- Search -->
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('CATALOG.PRODUCTS.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 ltr:!pl-8 !py-1',
            ]"
            class="w-64"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2"
              />
            </template>
          </Input>

          <div class="flex items-center gap-2">
            <!-- Filter button -->
            <div class="relative">
              <Button
                id="toggleCatalogFilterButton"
                icon="i-lucide-list-filter"
                color="slate"
                size="sm"
                variant="ghost"
                class="relative w-8"
                @click="openFilterPanel"
              >
                <div
                  v-if="hasActiveFilters"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>

              <!-- Filter panel — same style as ContactsFilter -->
              <div
                class="absolute mt-1 ltr:right-0 rtl:left-0 top-full"
                style="min-width: 400px;"
              >
                <div
                  v-if="showFilterPanel"
                  v-on-click-outside="filterOutsideHandler"
                  class="z-40 overflow-visible border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
                >
                  <h3 class="text-base font-medium leading-6 text-n-slate-12">
                    {{ t('CATALOG.PRODUCTS.FILTER.TITLE') }}
                  </h3>

                  <div class="grid gap-4">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-sm text-n-slate-12">
                        {{ t('CATALOG.PRODUCTS.FILTER.SOURCE_LABEL') }}
                      </span>
                      <SelectMenu
                        :model-value="pendingSourceId"
                        :options="sourceOptions"
                        :label="sourceLabelFor(pendingSourceId)"
                        sub-menu-position="bottom"
                        @update:model-value="pendingSourceId = $event"
                      />
                    </div>

                    <div class="flex items-center justify-between gap-2">
                      <span class="text-sm text-n-slate-12">
                        {{ t('CATALOG.PRODUCTS.FILTER.STATUS_LABEL') }}
                      </span>
                      <SelectMenu
                        :model-value="pendingStatus"
                        :options="statusOptions"
                        :label="statusLabelFor(pendingStatus)"
                        sub-menu-position="bottom"
                        @update:model-value="pendingStatus = $event"
                      />
                    </div>
                  </div>

                  <div class="flex justify-between gap-2">
                    <Button sm ghost blue class="flex-shrink-0" @click="clearFilters">
                      {{ t('CATALOG.PRODUCTS.FILTER.CLEAR') }}
                    </Button>
                    <Button sm solid blue @click="applyFilters">
                      {{ t('CATALOG.PRODUCTS.FILTER.APPLY') }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sort button — same style as ContactSortMenu -->
            <div class="relative">
              <Button
                icon="i-lucide-arrow-down-up"
                color="slate"
                size="sm"
                variant="ghost"
                :class="showSortMenu ? 'bg-n-alpha-2' : ''"
                @click="showSortMenu = !showSortMenu"
              />
              <div
                v-if="showSortMenu"
                v-on-click-outside="() => (showSortMenu = false)"
                class="absolute top-full mt-1 ltr:right-0 rtl:left-0 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-4 z-30"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm text-n-slate-12">
                    {{ t('CATALOG.PRODUCTS.SORT.LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="activeSort"
                    :options="sortOptions"
                    :label="activeSortLabel"
                    @update:model-value="handleSortChange"
                  />
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm text-n-slate-12">
                    {{ t('CATALOG.PRODUCTS.SORT.ORDER_LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="activeDirection"
                    :options="directionOptions"
                    :label="activeDirectionLabel"
                    @update:model-value="handleDirectionChange"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </header>

    <!-- Scrollable content — same as ContactsListLayout main -->
    <main class="flex-1 overflow-y-auto scrollbar-custom px-6 lg:px-16">
      <div class="w-full mx-auto max-w-7xl py-4">

        <div v-if="loading" class="flex items-center justify-center py-20">
          <Spinner />
        </div>

        <div
          v-else-if="error"
          class="px-4 py-3 rounded-xl bg-n-ruby-3 text-sm text-n-ruby-12"
        >
          {{ error }}
        </div>

        <PageEmptyState
          v-else-if="!products.length"
          icon="i-lucide-package"
          :title="t('CATALOG.PRODUCTS.EMPTY_TITLE')"
          :description="t('CATALOG.PRODUCTS.EMPTY_DESCRIPTION')"
        />

        <!-- Product list — gap-4 like ContactsList -->
        <div v-else class="flex flex-col gap-4 pb-4">
          <div
            v-for="product in products"
            :key="product.id"
            class="flex flex-col w-full outline-1 outline outline-n-container -outline-offset-1 rounded-xl bg-n-solid-2"
          >
            <!-- Product row -->
            <button
              type="button"
              class="flex w-full gap-4 px-6 py-4 flex-row justify-between items-center hover:bg-n-alpha-1 transition-colors rounded-xl"
              @click="toggleExpand(product.id)"
            >
              <!-- Image -->
              <img
                v-if="product.image_url"
                :src="product.image_url"
                :alt="product.title"
                class="size-11 rounded-lg object-cover shrink-0"
              />
              <div
                v-else
                class="size-11 rounded-lg bg-n-solid-3 flex items-center justify-center shrink-0"
              >
                <i class="i-lucide-image text-n-slate-9 text-lg" />
              </div>

              <!-- Info -->
              <div class="flex-1 min-w-0 text-left">
                <p class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ product.title }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5 truncate">
                  <span v-if="product.vendor">{{ `${product.vendor} · ` }}</span>
                  {{ sourceKindLabel(product.source?.kind) }}
                </p>
              </div>

              <!-- Right side -->
              <div class="flex items-center gap-3 shrink-0">
                <span class="text-xs px-2 py-0.5 rounded-full bg-n-solid-3 text-n-slate-11 whitespace-nowrap">
                  {{ product.variants?.length || 0 }}
                  {{ (product.variants?.length || 0) === 1 ? t('CATALOG.PRODUCTS.VARIANT') : t('CATALOG.PRODUCTS.VARIANTS') }}
                </span>
                <span class="text-sm font-medium text-n-slate-12 whitespace-nowrap tabular-nums w-28 text-right">
                  {{ priceRange(product) }}
                </span>
                <span
                  :class="productStatusColor(product.status)"
                  class="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap w-16 text-center"
                >
                  {{ productStatusText(product.status) }}
                </span>
                <i
                  class="i-lucide-chevron-down text-n-slate-9 text-sm transition-transform duration-200 shrink-0"
                  :class="{ 'rotate-180': expandedId === product.id }"
                />
              </div>
            </button>

            <!-- Variants panel -->
            <div
              v-if="expandedId === product.id"
              class="border-t border-n-container px-6 py-3"
            >
              <table class="w-full text-xs">
                <thead>
                  <tr class="text-n-slate-10 border-b border-n-container">
                    <th class="text-left font-medium pb-2 pr-3 w-9" />
                    <th class="text-left font-medium pb-2 pr-3">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.TITLE') }}</th>
                    <th class="text-left font-medium pb-2 pr-3 font-mono">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.SKU') }}</th>
                    <th class="text-right font-medium pb-2 pr-3">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.COMPARE_AT') }}</th>
                    <th class="text-right font-medium pb-2 pr-3">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.PRICE') }}</th>
                    <th class="text-right font-medium pb-2">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.STOCK') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-container">
                  <tr v-for="variant in product.variants" :key="variant.id">
                    <td class="py-2 pr-3">
                      <img
                        v-if="variant.image_url"
                        :src="variant.image_url"
                        :alt="variant.title"
                        class="size-8 rounded object-cover"
                      />
                      <div
                        v-else
                        class="size-8 rounded bg-n-solid-3 flex items-center justify-center"
                      >
                        <i class="i-lucide-image text-n-slate-9 text-xs" />
                      </div>
                    </td>
                    <td class="py-2 pr-3 text-n-slate-12 font-medium">{{ variant.title }}</td>
                    <td class="py-2 pr-3 text-n-slate-10 font-mono">{{ variant.sku || '—' }}</td>
                    <td class="py-2 pr-3 text-right text-n-slate-9 tabular-nums">
                      <span v-if="variant.compare_at_price" class="line-through">
                        {{ formatCurrency(variant.compare_at_price, variant.currency) }}
                      </span>
                      <span v-else>{{ DASH }}</span>
                    </td>
                    <td class="py-2 pr-3 text-right text-n-slate-12 tabular-nums font-medium">
                      {{ formatCurrency(variant.price, variant.currency) }}
                    </td>
                    <td
                      class="py-2 text-right tabular-nums font-medium"
                      :class="(variant.inventory_quantity ?? 0) > 0 ? 'text-n-teal-11' : 'text-n-ruby-11'"
                    >
                      {{ variant.inventory_quantity ?? '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- Sticky pagination footer — same as ContactsListLayout -->
    <footer v-if="!loading && total > perPage" class="sticky bottom-0 z-0">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="total"
        :items-per-page="perPage"
        @update:current-page="fetchProducts"
      />
    </footer>

  </section>
</template>
